import React, {Component, Fragment} from "react";
import {connect} from "react-redux";
import isEqual from "lodash/isEqual";

import TemplateTab from "../tabs/TemplateTab";
import ConfigTab from "../tabs/ConfigTab";
import * as templateHelper from "../helper/template";
import ErrorBoundary from "../common/ErrorBoundary";
import UploadLogoModal from "../config-items/partials/UploadLogoModal";
import {showNotification} from "../../../store/slices/admin/notification";
import {updateDashboard} from "../../../store/slices/admin/dashboards";
import {getDashboard} from "../../../store/slices/dashboard";
import {ITemplateConfig, IGridConfigItem} from "../../../constants/interfaces";
import { fetchBigscreenSettings } from "../../../store/slices/admin/bigscreenSettings";

type Props = {
	dashboard: ITemplateConfig,
	// injected
	dispatch: Function,
	match: {
		params: {id: string}
	}
}

type State = {
	activeTab: string,
	templateConfig:	 ITemplateConfig,
	templateId: string | number,
	renameActive: boolean,
	modalOpen: boolean,
	logo: {
		url: string,
		name: string
	} | null
}

class EditDashboard extends Component<Props, State> {
	private readonly templateId: number | string;
	constructor(props: Props) {
		super(props);

		this.templateId = this.props.match.params.id;

		this.state = {
			activeTab: "template",
			// @ts-ignore
			templateConfig:	 {},
			templateId: this.templateId,
			renameActive: false,
			modalOpen: false,
			logo: null
		}

		this.toggleRenameField = this.toggleRenameField.bind(this);
		this.changeName = this.changeName.bind(this);
		this.selectTemplate = this.selectTemplate.bind(this);
		this.updateConfig = this.updateConfig.bind(this);
		this.toggleModal = this.toggleModal.bind(this);
		this.uploadLogo = this.uploadLogo.bind(this);
		this.updateDashboard = this.updateDashboard.bind(this);
		this.switchTabViaTemplateDoubleClick = this.switchTabViaTemplateDoubleClick.bind(this);
	}

	componentDidMount() {
		document.title = "Edit Dashboard - Smartocto";
		document.body.dataset.titleReady = "true";
		this.props.dispatch(fetchBigscreenSettings());

		if (this.props.dashboard) {
			this.props.dispatch(getDashboard(this.templateId.toString()))
				.then(() => {
					this.setState({
						templateConfig: {...this.props.dashboard}
					})
				});
		}
	}

	componentDidUpdate(prevProps: Props, prevState: State) {
		if (!isEqual(prevProps.dashboard, this.props.dashboard)) {
			const logo = {...this.state.logo};
			const dashboard = {...this.props.dashboard};
			if (dashboard.image) {
				logo.url = dashboard.image;
			}
			this.setState({
				templateConfig: dashboard,
				// @ts-ignore
				logo
			});
		}

		if (prevState.templateId !== this.state.templateId) {
			this.setName();
		}
	}

	setName() {
		const templateConfig = {...this.state.templateConfig};
		if (!templateConfig.description) {
			templateConfig.description = this.props.dashboard.description;
			this.setState({templateConfig});
		}
	}

	switchTab(tab: string) {
		this.setState({activeTab: tab});
	}

	switchTabViaTemplateDoubleClick() {
        this.switchTab("config")
    }

	selectTemplate(templateId: string | number) {
		if (templateId === this.state.templateConfig.grid_type_id) return
		const dashboard = this.props.dashboard;
		const defaultTemplate = templateHelper.getTemplateDefaultConfiguration(templateId);
		let templateConfig: ITemplateConfig;
		let template;
		if (dashboard.grid_type_id === templateId) {
			templateConfig = defaultTemplate;
			templateConfig.id = dashboard.id;
			template = dashboard.id;
		} else {
			templateConfig = defaultTemplate;
			templateConfig.id = dashboard.id;
			template = templateId;
		}

		templateConfig = {
			...templateConfig,
			description : this.state.templateConfig.description
		}
		this.setState({
			templateId: template,
			templateConfig
		});
	}

	toggleRenameField() {
		this.setState({renameActive: !this.state.renameActive});
	}

	changeName(e: any) {
		let templateConfig = {...this.state.templateConfig};
		templateConfig.description = e.target.value;
		this.setState({templateConfig});
	}

	updateConfig(itemId: number, itemConfig: IGridConfigItem) {
		this.setState(prev => {
			let templateConfig = { ...prev.templateConfig };
			let gridConfig = [...templateConfig.grid_config];
			
			const existingItem = gridConfig[Number(itemId)] || {};

			gridConfig[Number(itemId)] = {
				...existingItem,
				...itemConfig
			};

			templateConfig.grid_config = gridConfig;

			return { templateConfig };
		});
	}

	toggleModal() {
		this.setState({
			modalOpen: !this.state.modalOpen
		})
	}

	uploadLogo(e: any) {
		const file = e.target.files[0];
		const isValid = this.validateLogo(file);
		if (isValid) {
			const logo = {...this.state.logo};
			let fileReader = new FileReader();
			fileReader.onloadend = (e) => {
				// @ts-ignore
				logo.url = e.target.result;
				logo.name = file.name;
				// @ts-ignore
				this.setState({logo});
			};
			fileReader.readAsDataURL(file);
		} else {
			e.target.value = null;
			this.props.dispatch(showNotification('error', 'Invalid file extension.'));
		}
	}

	validateLogo(file: {name: string}) {
		const validExtensions = ['jpg', 'png', 'jpeg', 'svg'];
		const fileName =  file.name;
		const fileExt = fileName.substr(fileName.lastIndexOf('.') + 1);
		return validExtensions.indexOf(fileExt) !== -1;
	}

	updateDashboard() {
		const config = {...this.state.templateConfig};
		const logo = {...this.state.logo};
		const data = {
			description: config.description,
			message : config.message,
			brands: config.brands,
			grid_type_id: config.grid_type_id,
			grid_type: config.grid_type,
			grid_config: config.grid_config,
			image: logo.url
		};

		this.props.dispatch(updateDashboard(config.id, data));
	}

	render() {
		const description = this.state.templateConfig.description;
		return (
			<div className="container create-dashboard">
				<div className="row">
					<div className="col-md-12">
						<div className="create-dashboard__header">
							{
								this.state.renameActive ? (
									<Fragment>
										<input type="text" className="form-control" value={description} onChange={this.changeName}/>
										<button className="btn btn-outline-primary" onClick={this.toggleRenameField}>
											Save
										</button>
									</Fragment>
								) : (
									<Fragment>
										<h2 title={description}>{description}</h2>
										<button className="btn btn-outline-primary" onClick={this.toggleRenameField}>
											Rename
										</button>
									</Fragment>
								)
							}
							<div className="ml-auto">
								<button className="btn btn-outline-primary mr-2" onClick={this.toggleModal}>
									Update logo
								</button>
								<button className="btn btn-primary" onClick={this.updateDashboard}>
									Update dashboard
								</button>
							</div>
						</div>
					</div>

					<div className="col-md-12">
						<div className="create-dashboard__flow">
							<div className="steps">
								<div
									className={`step-one ${this.state.activeTab === "template" ? "active" : ""}`}
									onClick={() => this.switchTab("template")}
								>
									<span>1.Pick template</span>
									<div className="arrow-right"></div>
								</div>
								<div
									className={`step-two ${this.state.activeTab === "config" ? "active" : ""}`}
									onClick={() => this.switchTab("config")}
								>
									<span>2.Configure components</span>
									<div className="arrow-right"></div>
								</div>
							</div>
							{
								this.state.activeTab === "template" ? (
									<button className="btn btn-primary next-step" onClick={() => this.switchTab("config")}>Next step</button>
								) : (
									<button className="btn btn-primary next-step" onClick={() => this.switchTab("template")}>Back</button>
								)
							}

						</div>
					</div>

					<div className="col-md-12">
						<ErrorBoundary>
							<TemplateTab
								selectTemplate={this.selectTemplate}
								isActive={this.state.activeTab === "template"}
								templateId={this.state.templateConfig.grid_type_id}
								isEdit={true}
								switchTab={this.switchTabViaTemplateDoubleClick}
							/>
						</ErrorBoundary>
					</div>

					<div className="col-md-12">
						<ErrorBoundary>
							<ConfigTab
								isActive={this.state.activeTab === "config"}
								updateConfig={this.updateConfig}
								templateConfig={this.state.templateConfig}
								isEdit={true}
							/>
						</ErrorBoundary>
					</div>
				</div>
				<UploadLogoModal
					isOpen={this.state.modalOpen}
					logo={this.state.logo}
					toggleModal={this.toggleModal}
					uploadLogo={this.uploadLogo}
				/>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => {
	return {
		dashboard: state.dashboard.data,
		isLoading: state.dashboard.isLoading
	}
};

export default connect(mapStateToProps)(EditDashboard);
